/**
 * SKI AXIS · /programmes — Umbrella page for all six programmes.
 *
 * Three things in one page:
 *   1. The Thesis · how the six programmes interlock as a single founder journey
 *   2. The 30-second Self-Quiz · routes a visitor to the right programme entry point
 *   3. The Six Programme Cards · with stage/persona context + direct entry CTAs
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Rocket, Award, Trophy, GraduationCap, Globe2, Layers,
  CheckCircle2, RotateCcw, Sparkles, Mail,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.pgm-root{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;}
.pgm-shell{max-width:1180px;margin:0 auto;padding:64px 32px;}
@media(max-width:680px){.pgm-shell{padding:40px 22px;}}

.pgm-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:22px;}
.pgm-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.pgm-h1{font-family:'Cormorant Display',serif;font-size:clamp(46px,6.5vw,82px);font-weight:700;line-height:.98;letter-spacing:-.025em;margin:0 0 22px;color:#0D1B2A;max-width:1100px;}
.pgm-h1 em{font-style:italic;color:#C8A84B;font-weight:600;}
.pgm-sub{font-size:19px;line-height:1.55;color:#3D4A5C;max-width:820px;font-weight:300;margin:0 0 32px;}

/* JOURNEY MAP */
.pgm-journey{margin-top:80px;background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:56px 56px;border-left:4px solid #C8A84B;position:relative;overflow:hidden;}
@media(max-width:680px){.pgm-journey{padding:38px 26px;}}
.pgm-journey::before{content:'';position:absolute;top:-80px;right:-80px;width:300px;height:300px;background:radial-gradient(circle,rgba(200,168,75,.13) 0%,transparent 70%);}
.pgm-journey-eb{font-size:11px;letter-spacing:.32em;font-weight:700;color:#C8A84B;text-transform:uppercase;margin-bottom:14px;display:flex;align-items:center;gap:14px;}
.pgm-journey-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.pgm-journey-h{font-family:'Cormorant Display',serif;font-size:clamp(30px,4vw,46px);font-weight:700;line-height:1.1;color:#FAF7F0;letter-spacing:-.014em;margin:0 0 16px;max-width:880px;}
.pgm-journey-h em{font-style:italic;color:#C8A84B;}
.pgm-journey-p{font-size:16px;line-height:1.7;color:rgba(250,247,240,.78);font-weight:300;margin:0 0 36px;max-width:840px;}
.pgm-journey-p strong{color:#C8A84B;font-weight:700;}
.pgm-rail{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-top:8px;}
@media(max-width:980px){.pgm-rail{grid-template-columns:repeat(3,1fr);}}
@media(max-width:560px){.pgm-rail{grid-template-columns:repeat(2,1fr);}}
.pgm-stop{background:rgba(255,255,255,.04);border:1px solid rgba(200,168,75,.22);padding:18px 16px;display:flex;flex-direction:column;gap:6px;cursor:pointer;transition:all .18s;}
.pgm-stop:hover{border-color:#C8A84B;transform:translateY(-3px);background:rgba(200,168,75,.06);}
.pgm-stop-num{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:13px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;}
.pgm-stop-name{font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;color:#FAF7F0;line-height:1.18;letter-spacing:-.005em;margin:2px 0 4px;}
.pgm-stop-name em{color:#C8A84B;font-style:italic;}
.pgm-stop-d{font-size:12px;color:rgba(250,247,240,.68);line-height:1.5;margin:0;}

/* QUIZ */
.pgm-quiz{margin-top:96px;}
.pgm-h2{font-family:'Cormorant Display',serif;font-size:clamp(36px,4.5vw,54px);font-weight:700;line-height:1.04;letter-spacing:-.018em;margin:0 0 14px;color:#0D1B2A;}
.pgm-h2 em{font-style:italic;color:#C8A84B;}
.pgm-h2-sub{font-size:15.5px;color:#3D4A5C;max-width:820px;margin:0 0 32px;line-height:1.55;}
.pgm-card{background:#fff;border:1px solid #E8DFC9;border-left:4px solid #C8A84B;padding:36px 40px;margin-bottom:14px;}
@media(max-width:680px){.pgm-card{padding:26px 24px;}}
.pgm-q-eb{font-size:11px;letter-spacing:.28em;color:#C8A84B;font-weight:800;text-transform:uppercase;margin-bottom:10px;}
.pgm-q-h{font-family:'Cormorant Display',serif;font-size:30px;font-weight:700;color:#0D1B2A;line-height:1.15;letter-spacing:-.012em;margin:0 0 22px;}
.pgm-q-h em{font-style:italic;color:#C8A84B;}
.pgm-opts{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
@media(max-width:680px){.pgm-opts{grid-template-columns:1fr;}}
.pgm-opt{display:flex;align-items:center;gap:14px;padding:18px 22px;border:1px solid #E8DFC9;background:#FAF7F0;cursor:pointer;transition:all .15s;text-align:left;font-family:inherit;}
.pgm-opt:hover{border-color:#C8A84B;background:#FBF8EE;}
.pgm-opt.sel{border-color:#C8A84B;background:#FBF8EE;border-left:3px solid #C8A84B;}
.pgm-opt-num{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-weight:700;font-size:18px;flex-shrink:0;}
.pgm-opt-lbl{font-size:14.5px;color:#0D1B2A;line-height:1.5;font-weight:500;}
.pgm-progress{display:flex;gap:6px;margin-bottom:24px;}
.pgm-progress-step{flex:1;height:3px;background:#E8DFC9;}
.pgm-progress-step.active{background:#C8A84B;}
.pgm-quiz-actions{display:flex;justify-content:space-between;align-items:center;margin-top:20px;}
.pgm-back{display:inline-flex;align-items:center;gap:8px;color:#3D4A5C;background:transparent;border:none;font-family:inherit;font-size:11.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;cursor:pointer;padding:6px 8px;}
.pgm-back:hover{color:#0D1B2A;}

/* RESULT */
.pgm-result{background:#fff;border:1px solid #C8A84B;border-left:4px solid #C8A84B;padding:36px 40px;margin-bottom:14px;}
@media(max-width:680px){.pgm-result{padding:26px 24px;}}
.pgm-result-eb{font-size:11px;letter-spacing:.32em;color:#C8A84B;font-weight:800;text-transform:uppercase;margin-bottom:10px;display:flex;align-items:center;gap:10px;}
.pgm-result-h{font-family:'Cormorant Display',serif;font-size:34px;font-weight:700;color:#0D1B2A;line-height:1.1;letter-spacing:-.014em;margin:0 0 12px;}
.pgm-result-h em{font-style:italic;color:#C8A84B;}
.pgm-result-why{font-size:15px;line-height:1.7;color:#3D4A5C;font-weight:300;margin:0 0 22px;}
.pgm-result-why strong{color:#0D1B2A;font-weight:700;}
.pgm-result-cta{display:inline-flex;align-items:center;gap:10px;background:#0D1B2A;color:#C8A84B;padding:18px 32px;font-size:12px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;border:1px solid #0D1B2A;cursor:pointer;font-family:inherit;transition:all .2s;}
.pgm-result-cta:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.pgm-result-row{display:flex;flex-wrap:wrap;gap:14px;align-items:center;}
.pgm-restart{display:inline-flex;align-items:center;gap:8px;color:#3D4A5C;background:transparent;border:1px solid rgba(13,27,42,.2);font-family:inherit;font-size:11.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;cursor:pointer;padding:16px 24px;}
.pgm-restart:hover{border-color:#C8A84B;color:#C8A84B;}

/* EMAIL CAPTURE (after result) */
.pgm-cap{margin-top:22px;padding-top:22px;border-top:1px dashed rgba(200,168,75,.45);}
.pgm-cap-eb{font-size:10.5px;letter-spacing:.28em;color:#C8A84B;font-weight:800;text-transform:uppercase;margin-bottom:8px;}
.pgm-cap-h{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#0D1B2A;line-height:1.2;letter-spacing:-.008em;margin:0 0 6px;}
.pgm-cap-h em{font-style:italic;color:#C8A84B;}
.pgm-cap-p{font-size:13px;line-height:1.6;color:#3D4A5C;font-weight:300;margin:0 0 16px;max-width:620px;}
.pgm-cap-row{display:flex;gap:8px;flex-wrap:wrap;align-items:stretch;max-width:620px;}
.pgm-cap-input{flex:1;min-width:240px;padding:14px 16px;border:1px solid #E8DFC9;background:#FAF7F0;font-family:inherit;font-size:14px;color:#0D1B2A;outline:none;transition:border-color .15s;}
.pgm-cap-input:focus{border-color:#C8A84B;background:#fff;}
.pgm-cap-input::placeholder{color:#A89F8E;font-style:italic;}
.pgm-cap-btn{display:inline-flex;align-items:center;gap:8px;background:#0D1B2A;color:#C8A84B;padding:0 24px;font-size:11px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;border:1px solid #0D1B2A;cursor:pointer;font-family:inherit;transition:all .18s;white-space:nowrap;}
.pgm-cap-btn:hover:not(:disabled){background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.pgm-cap-btn:disabled{opacity:.6;cursor:not-allowed;}
.pgm-cap-success{display:flex;align-items:center;gap:10px;padding:14px 18px;background:rgba(200,168,75,.12);border-left:3px solid #C8A84B;color:#0D1B2A;font-size:13.5px;line-height:1.55;font-weight:500;max-width:620px;}
.pgm-cap-success em{font-style:italic;color:#8A6A14;font-weight:600;}
.pgm-cap-error{margin-top:10px;font-size:12.5px;color:#B0341E;font-weight:500;}
.pgm-cap-fine{font-size:11px;color:#7A8190;margin-top:10px;font-style:italic;}

/* SIX PROGRAMME CARDS */
.pgm-cards{margin-top:96px;}
.pgm-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-top:28px;}
@media(max-width:980px){.pgm-grid{grid-template-columns:repeat(2,1fr);}}
@media(max-width:560px){.pgm-grid{grid-template-columns:1fr;}}
.pgm-prg{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:32px 28px;display:flex;flex-direction:column;gap:14px;transition:transform .18s,box-shadow .18s;}
.pgm-prg:hover{transform:translateY(-3px);box-shadow:0 14px 30px rgba(13,27,42,.06);}
.pgm-prg-icn{width:44px;height:44px;display:flex;align-items:center;justify-content:center;border-radius:8px;background:rgba(200,168,75,.12);color:#C8A84B;}
.pgm-prg-eb{font-size:10.5px;letter-spacing:.28em;color:#C8A84B;font-weight:800;text-transform:uppercase;}
.pgm-prg-name{font-family:'Cormorant Display',serif;font-size:26px;font-weight:700;color:#0D1B2A;line-height:1.1;letter-spacing:-.012em;margin:0;}
.pgm-prg-name em{font-style:italic;color:#C8A84B;}
.pgm-prg-tag{font-family:'Cormorant Display',serif;font-style:italic;font-size:15px;color:#8A6A14;line-height:1.4;}
.pgm-prg-d{font-size:13.5px;color:#3D4A5C;line-height:1.6;margin:6px 0 0;}
.pgm-prg-meta{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;}
.pgm-prg-chip{font-size:10px;letter-spacing:.16em;font-weight:700;color:#0D1B2A;background:#FBF8EE;padding:4px 10px;border:1px solid #E8DFC9;text-transform:uppercase;}
.pgm-prg-cta{margin-top:auto;display:inline-flex;align-items:center;gap:8px;font-size:11px;font-weight:800;letter-spacing:.18em;color:#0D1B2A;text-transform:uppercase;padding-top:14px;border-top:1px solid #FAF0DC;font-family:inherit;background:transparent;border-bottom:0;border-left:0;border-right:0;cursor:pointer;text-align:left;}
.pgm-prg-cta:hover{color:#C8A84B;}
`;

// =================== JOURNEY MAP DATA ===================
const JOURNEY = [
  { num: 'Stop 01', icn: <Layers />, name: 'Accelerator', d: 'The 12-week governance-first programme. The chassis everything else hangs on.', path: '/accelerator' },
  { num: 'Stop 02', icn: <Rocket />, name: 'Cohort 1', d: 'July 2026. 20 chosen founders. The first class to walk the framework end-to-end.', path: '/apply' },
  { num: 'Stop 03', icn: <Award />, name: <>The <em>Axis Index™</em></>, d: 'The measurable governance maturity score. 30% of every Circuit jury verdict.', path: '/axis-index' },
  { num: 'Stop 04', icn: <GraduationCap />, name: 'Masterclass', d: 'Ongoing live + recorded faculty sessions. The continuing education layer.', path: '/masterclass' },
  { num: 'Stop 05', icn: <Globe2 />, name: 'Chapter Leads', d: '11 city anchors. The local on-the-ground network everywhere founders are building.', path: '/chapters' },
  { num: 'Stop 06', icn: <Trophy />, name: <>The <em>Axis Circuit™</em></>, d: 'The annual tournament. Demo Day → Salons → Dubai Grand Finale. The reward layer.', path: '/axis-circuit' },
];

// =================== QUIZ ===================
const QUESTIONS = [
  {
    id: 'persona',
    eb: 'Question 1 of 3',
    q: <>Which describes you <em>best</em> right now?</>,
    options: [
      { value: 'founder_pre_seed', label: 'Founder · pre-seed or idea-stage' },
      { value: 'founder_seed_a',   label: 'Founder · seed / Series A · already raised' },
      { value: 'founder_growth',   label: 'Founder · growth · post-Series A' },
      { value: 'investor',         label: 'Investor · GP / family office / angel' },
      { value: 'operator',         label: 'Operator · ecosystem / fund / accelerator background' },
      { value: 'curious',          label: 'Just exploring · interested in governance' },
    ],
  },
  {
    id: 'goal',
    eb: 'Question 2 of 3',
    q: <>What do you most want from us in the <em>next twelve weeks</em>?</>,
    options: [
      { value: 'institutional_investor_ready', label: 'To be institutional / investor-ready' },
      { value: 'measure_governance',           label: 'To measure my governance — Bronze score' },
      { value: 'compete',                      label: 'To compete · access term sheets · grow brand' },
      { value: 'learn_faculty',                label: 'To learn from faculty without the full cohort' },
      { value: 'host_anchor',                  label: 'To host SKI Axis in my city · earn equity + revshare' },
      { value: 'understand',                   label: 'Just to understand how it all fits together' },
    ],
  },
  {
    id: 'jurisdiction',
    eb: 'Question 3 of 3',
    q: <>Where are you <em>building</em> from?</>,
    options: [
      { value: 'uae',          label: 'UAE · Dubai / Abu Dhabi / Sharjah' },
      { value: 'gcc',          label: 'GCC · Riyadh / Doha / Kuwait / elsewhere' },
      { value: 'india',        label: 'India · Mumbai / Bangalore / Delhi' },
      { value: 'asia',         label: 'Wider Asia · Singapore / KL / Jakarta / etc.' },
      { value: 'emea',         label: 'EMEA · London / Amsterdam / Lagos / Nairobi / etc.' },
      { value: 'americas',     label: 'Americas · NYC / Toronto / São Paulo / etc.' },
    ],
  },
];

// =================== ROUTING LOGIC ===================
const computeMatch = (answers) => {
  const { persona, goal } = answers;
  // Investors → Axis Index investor track
  if (persona === 'investor') {
    return {
      programme: 'axis-index',
      h: <>The <em>Axis Index™</em> · Investor track</>,
      eb: 'Best fit',
      icn: <Award className="h-5 w-5" />,
      why: <>The <strong>Investors Circle Bronze flywheel</strong> lets you pre-screen any company you're considering against a measurable governance maturity index. Indexing as due diligence — your due diligence becomes the founder's improvement plan.</>,
      cta: 'Read The Axis Index',
      path: '/axis-index',
    };
  }
  // Operator wanting to host
  if (persona === 'operator' || goal === 'host_anchor') {
    return {
      programme: 'chapters',
      h: <>Chapter <em>Leads</em></>,
      eb: 'Best fit',
      icn: <Globe2 className="h-5 w-5" />,
      why: <>You bring the network · we bring the institution. <strong>20% revenue share + 1% equity per founder placed</strong>, plus four secondary income streams. We're appointing one Chapter Lead per corridor — the application takes 10 minutes.</>,
      cta: 'Apply as a Chapter Lead',
      path: '/chapters/apply',
    };
  }
  // Curious / learn-only
  if (persona === 'curious' || goal === 'understand' || goal === 'learn_faculty') {
    return {
      programme: 'masterclass',
      h: 'Masterclass',
      eb: 'Lowest commitment · highest curiosity',
      icn: <GraduationCap className="h-5 w-5" />,
      why: <>Live + recorded faculty sessions on governance, capital, AI, and operating systems. <strong>No application, no equity, no commitment</strong> — pay per session or watch the recordings. The on-ramp to everything else.</>,
      cta: 'Browse Masterclass',
      path: '/masterclass',
    };
  }
  // Founder · measure governance
  if (goal === 'measure_governance') {
    return {
      programme: 'axis-index',
      h: <>The <em>Axis Index™</em></>,
      eb: 'Direct match',
      icn: <Award className="h-5 w-5" />,
      why: <>Bronze self-assessment opens Q1 2027 to the public — but Cohort 1 founders are auto-indexed at intake. <strong>If you're not in Cohort 1, the Bronze waitlist is the fastest path to your first measurable score.</strong></>,
      cta: 'Read The Axis Index',
      path: '/axis-index',
    };
  }
  // Founder · compete
  if (goal === 'compete') {
    return {
      programme: 'axis-circuit',
      h: <>The <em>Axis Circuit™</em></>,
      eb: 'Best fit',
      icn: <Trophy className="h-5 w-5" />,
      why: <>The global tournament: 11 Chapter Qualifiers → 3 Regional Salons → Dubai Grand Finale Q4 2027. <strong>$50K Axis Prize · committed term sheets · permanent Hall of Fame.</strong> Cohort 1 founders auto-eligible; everyone else competes via Chapter Lead endorsement.</>,
      cta: 'Read The Axis Circuit',
      path: '/axis-circuit',
    };
  }
  // Default · founder, institutional-ready goal
  return {
    programme: 'cohort-1',
    h: <>Cohort <em>1</em> · July 2026</>,
    eb: 'Best fit',
    icn: <Rocket className="h-5 w-5" />,
    why: <>Cohort 1 is the chassis: 12 weeks · institutional-grade governance scaffolding · auto-index at intake · auto-eligible for the Axis Circuit · permanent Founding Cohort recognition. <strong>20 chosen founders. Application closes 1 June 2026.</strong></>,
    cta: 'Apply to Cohort 1',
    path: '/apply',
  };
};

// =================== SIX PROGRAMME CARDS ===================
const PROGRAMMES = [
  {
    icn: <Rocket className="h-5 w-5" />, eb: '01 · The Programme',
    name: 'Cohort 1',
    tag: '12-week institutional cohort · July 2026',
    d: 'The first class to walk the SKI Axis governance framework end-to-end. 20 chosen founders, anchored in Dubai, with auto-Bronze indexing at intake and auto-eligibility for the Axis Circuit.',
    chips: ['12 Weeks', 'Dubai', '20 Founders', 'Equity-based'],
    cta: 'Apply to Cohort 1', path: '/apply',
  },
  {
    icn: <Award className="h-5 w-5" />, eb: '02 · The Standard',
    name: <>The <em>Axis Index™</em></>,
    tag: 'Governance maturity framework · CC-Attribution',
    d: 'Five dimensions × four levels. A 20-cell rubric matrix. A 0–100 score that reads like a credit rating. The measurable layer that anchors everything else.',
    chips: ['Bronze · Free', 'Silver · Cohort', 'Gold · Audit', 'IIT Delhi'],
    cta: 'Read The Axis Index', path: '/axis-index',
  },
  {
    icn: <Trophy className="h-5 w-5" />, eb: '03 · The Tournament',
    name: <>The <em>Axis Circuit™</em></>,
    tag: 'Global governance-first competition · annual',
    d: '11 Chapter Qualifiers → 3 Regional Salons → Grand Finale (Q4 2027 · paired with Invest UAE Forum). USD $100K+ cash + committed term sheets + Hall of Fame. The reward layer for the cohort journey.',
    chips: ['11 Cities', '$100K+ Pool', 'Term Sheets', 'Hall of Fame'],
    cta: 'Read The Axis Circuit', path: '/axis-circuit',
  },
  {
    icn: <GraduationCap className="h-5 w-5" />, eb: '04 · The Curriculum',
    name: 'Masterclass',
    tag: 'Live + recorded faculty sessions · ongoing',
    d: 'No application, no equity, no commitment. Pay per session or join the full series. Faculty include institutional investors, sovereign-fund operators, AI-native founders, and regulators. The on-ramp before — and the continuing-education layer after — Cohort.',
    chips: ['Pay-per-session', 'Recorded', 'No equity', 'Open access'],
    cta: 'Browse Masterclass', path: '/masterclass',
  },
  {
    icn: <Globe2 className="h-5 w-5" />, eb: '05 · The Network',
    name: 'Chapter Leads',
    tag: 'Local anchors · 11 cities · now appointing',
    d: 'One Chapter Lead per corridor: Dubai · London · Amsterdam · Mumbai · Bangalore · Riyadh · Doha · Singapore · KL · Nairobi · Lagos. 20% revenue share + 1% equity per founder placed + a permanent Founding Chapter Lead position.',
    chips: ['11 Corridors', '20% Rev-share', '1% Equity', 'Founding'],
    cta: 'Apply as Chapter Lead', path: '/chapters/apply',
  },
  {
    icn: <Layers className="h-5 w-5" />, eb: '06 · The Chassis',
    name: 'Accelerator Overview',
    tag: 'How the 12-week programme actually runs',
    d: 'Week-by-week curriculum. Faculty roster. Governance scaffolding. Capital readiness. AI ops. The thinking behind why Cohort 1 was designed this way — and what every founder leaves with at Week 12 graduation.',
    chips: ['Curriculum', 'Faculty', 'Outcomes', 'Methodology'],
    cta: 'Read the Overview', path: '/accelerator',
  },
];

// =================== PAGE ===================
export default function ProgrammesPage() {
  useSEO({
    title: 'Programmes · SKI Axis · Six Paths Through One Institution',
    description: 'Cohort 1 · The Axis Index™ · The Axis Circuit™ · Masterclass · Chapter Leads · Accelerator. Six programmes that compose one founder journey. Take the 30-second self-quiz to find your fit.',
  });
  const navigate = useNavigate();
  const [step, setStep] = useState(0);  // 0–2 = questions; 3 = result
  const [answers, setAnswers] = useState({});

  // Email capture state (after the quiz result)
  const [capName, setCapName] = useState('');
  const [capEmail, setCapEmail] = useState('');
  const [capLoading, setCapLoading] = useState(false);
  const [capSuccess, setCapSuccess] = useState(false);
  const [capError, setCapError] = useState('');

  const onAnswer = (qid, value) => {
    const next = { ...answers, [qid]: value };
    setAnswers(next);
    setStep(step + 1);
  };

  const result = useMemo(() => {
    if (step < QUESTIONS.length) return null;
    return computeMatch(answers);
  }, [step, answers]);

  const onCaptureSubmit = async (e) => {
    e.preventDefault();
    setCapError('');
    if (!capEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(capEmail.trim())) {
      setCapError('Please enter a valid email.');
      return;
    }
    setCapLoading(true);
    try {
      await axios.post(`${API}/programmes/quiz-lead`, {
        full_name: capName.trim() || null,
        email: capEmail.trim(),
        persona: answers.persona || null,
        goal: answers.goal || null,
        jurisdiction: answers.jurisdiction || null,
        programme: result?.programme || null,
        programme_label: typeof result?.h === 'string' ? result.h : (result?.programme || null),
      });
      setCapSuccess(true);
    } catch (err) {
      setCapError(err?.response?.data?.detail || 'Could not send. Please try again.');
    } finally {
      setCapLoading(false);
    }
  };

  const onRestart = () => {
    setStep(0);
    setAnswers({});
    setCapName('');
    setCapEmail('');
    setCapSuccess(false);
    setCapError('');
  };

  return (
    <div className="pgm-root" data-testid="programmes-page">
      <style>{STYLES}</style>
      <Header />

      <div className="pgm-shell">
        {/* HERO */}
        <div className="pgm-eb">Programmes · v1 · The institutional layer</div>
        <h1 className="pgm-h1">
          Six programmes. <em>One thesis.</em>
        </h1>
        <p className="pgm-sub">
          Cohort 1 is the chassis. The Axis Index measures the work. The Axis Circuit rewards the work.
          Masterclass teaches the work. Chapter Leads anchor the work in 11 cities. The Accelerator overview
          explains how the chassis was built. They are designed to interlock — but you do not have to enter
          all six on day one.
        </p>

        {/* JOURNEY MAP */}
        <div className="pgm-journey" data-testid="pgm-journey">
          <div className="pgm-journey-eb">The Journey · Six Stops</div>
          <h2 className="pgm-journey-h">A founder enters at <em>one stop</em> · graduates through several.</h2>
          <p className="pgm-journey-p">
            Most founders begin at <strong>Cohort 1</strong> or the <strong>Masterclass</strong>. Each stop unlocks the next.
            Investors enter sideways at the Index. Operators enter sideways at Chapter Leads. The Circuit is the
            annual finale that pulls everyone together — Cohort founders, Index-graded operators, Chapter alumni — into one Dubai Grand Finale stage.
          </p>
          <div className="pgm-rail">
            {JOURNEY.map((j, i) => (
              <button key={i} className="pgm-stop" onClick={() => navigate(j.path)} data-testid={`pgm-stop-${i}`}>
                <div className="pgm-stop-num">{j.num}</div>
                <h3 className="pgm-stop-name">{j.name}</h3>
                <p className="pgm-stop-d">{j.d}</p>
              </button>
            ))}
          </div>
        </div>

        {/* QUIZ / RESULT */}
        <div className="pgm-quiz" data-testid="pgm-quiz">
          <h2 className="pgm-h2">Or — find your fit in <em>thirty seconds</em>.</h2>
          <p className="pgm-h2-sub">Three questions. We route you to the single right entry point. No email gate, no marketing pollution — answer and we'll show you where to start.</p>

          {step < QUESTIONS.length ? (
            <div className="pgm-card" data-testid={`pgm-q-${step}`}>
              <div className="pgm-progress">
                {QUESTIONS.map((_, i) => <div key={i} className={`pgm-progress-step ${i <= step ? 'active' : ''}`} />)}
              </div>
              <div className="pgm-q-eb">{QUESTIONS[step].eb}</div>
              <h3 className="pgm-q-h">{QUESTIONS[step].q}</h3>
              <div className="pgm-opts">
                {QUESTIONS[step].options.map((o, i) => (
                  <button key={o.value} className={`pgm-opt ${answers[QUESTIONS[step].id] === o.value ? 'sel' : ''}`} onClick={() => onAnswer(QUESTIONS[step].id, o.value)} data-testid={`pgm-q${step}-opt-${o.value}`}>
                    <span className="pgm-opt-num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="pgm-opt-lbl">{o.label}</span>
                  </button>
                ))}
              </div>
              {step > 0 && (
                <div className="pgm-quiz-actions">
                  <button className="pgm-back" onClick={() => setStep(step - 1)} data-testid="pgm-q-back">← Back</button>
                  <span></span>
                </div>
              )}
            </div>
          ) : result ? (
            <div className="pgm-result" data-testid="pgm-result">
              <div className="pgm-result-eb"><Sparkles className="h-3.5 w-3.5" /> {result.eb}</div>
              <h3 className="pgm-result-h">{result.h}</h3>
              <p className="pgm-result-why">{result.why}</p>
              <div className="pgm-result-row">
                <button className="pgm-result-cta" onClick={() => navigate(result.path)} data-testid="pgm-result-cta">
                  {result.cta} <ArrowRight className="h-4 w-4" />
                </button>
                <button className="pgm-restart" onClick={onRestart} data-testid="pgm-restart">
                  <RotateCcw className="h-3.5 w-3.5" /> Restart
                </button>
              </div>

              {/* OPTIONAL EMAIL CAPTURE */}
              <div className="pgm-cap" data-testid="pgm-cap">
                {capSuccess ? (
                  <div className="pgm-cap-success" data-testid="pgm-cap-success">
                    <CheckCircle2 className="h-4 w-4" style={{ color: '#8A6A14', flexShrink: 0 }} />
                    <span>Thanks! Your <em>1-page programme brief</em> is being prepared — it will land in your inbox within five minutes (a personal note from Nitish, with the matching PDF attached).</span>
                  </div>
                ) : (
                  <>
                    <div className="pgm-cap-eb"><Mail className="h-3 w-3" style={{ display: 'inline', marginRight: 6 }} />Optional · 1-page PDF brief</div>
                    <h4 className="pgm-cap-h">Want this routing as a <em>1-page PDF brief</em>?</h4>
                    <p className="pgm-cap-p">
                      Drop your email and we'll send you the matching programme one-pager — plus a short personal note from the team. No newsletter, no marketing pollution. One email, one PDF.
                    </p>
                    <form className="pgm-cap-row" onSubmit={onCaptureSubmit}>
                      <input
                        type="text"
                        className="pgm-cap-input"
                        placeholder="Your name (optional)"
                        value={capName}
                        onChange={(e) => setCapName(e.target.value)}
                        disabled={capLoading}
                        data-testid="pgm-cap-name"
                        style={{ flex: '0 0 200px' }}
                      />
                      <input
                        type="email"
                        className="pgm-cap-input"
                        placeholder="you@company.com"
                        value={capEmail}
                        onChange={(e) => setCapEmail(e.target.value)}
                        required
                        disabled={capLoading}
                        data-testid="pgm-cap-email"
                      />
                      <button
                        type="submit"
                        className="pgm-cap-btn"
                        disabled={capLoading}
                        data-testid="pgm-cap-submit"
                      >
                        {capLoading ? 'Sending…' : <>Send my brief <ArrowRight className="h-3.5 w-3.5" /></>}
                      </button>
                    </form>
                    {capError && <div className="pgm-cap-error" data-testid="pgm-cap-error">{capError}</div>}
                    <div className="pgm-cap-fine">We'll only use this to send your programme brief and one short follow-up. Reply "remove me" any time.</div>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* SIX PROGRAMME CARDS */}
        <div className="pgm-cards" data-testid="pgm-cards">
          <h2 className="pgm-h2">Or — <em>browse</em> the six programmes.</h2>
          <p className="pgm-h2-sub">Each card is the entry point for one programme. Stage tags · what's included · who it's for · direct CTA into the programme's own page.</p>
          <div className="pgm-grid">
            {PROGRAMMES.map((p, i) => (
              <div key={i} className="pgm-prg" data-testid={`pgm-prg-${i}`}>
                <div className="pgm-prg-icn">{p.icn}</div>
                <div className="pgm-prg-eb">{p.eb}</div>
                <h3 className="pgm-prg-name">{p.name}</h3>
                <div className="pgm-prg-tag">{p.tag}</div>
                <p className="pgm-prg-d">{p.d}</p>
                <div className="pgm-prg-meta">{p.chips.map((c) => <span key={c} className="pgm-prg-chip">{c}</span>)}</div>
                <button className="pgm-prg-cta" onClick={() => navigate(p.path)} data-testid={`pgm-prg-cta-${i}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> {p.cta} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
